/* eslint-disable @typescript-eslint/no-explicit-any */
/* "Suggested visuals" for an (empty) page (contract section 7). Rules-based:
 * the server profiles the dataset's real rows and lists individual visuals,
 * each with a plain-words reason and an Add button. Mounted by the studio's
 * empty-canvas state (workstream B); written by workstream D. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Database, Filter, Hash, LineChart, Loader2, PieChart, Plus, Sparkles, Table2, X } from "lucide-react";
import { useMemo, useState } from "react";

import { datasetApi } from "../api/dataSources";
import { suggestApi, type SuggestedVisual } from "../api/v21";
import { useAuth } from "../auth/AuthContext";
import { Banner, Button, EmptyState, Select, SkeletonRows, useToast } from "../ds";
import { modeFor } from "../shell/role";

const GROUP_ICON: Record<string, typeof Hash> = { Numbers: Hash, Trends: LineChart, Breakdowns: PieChart, Tables: Table2, Filters: Filter };

function freshId(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

export function SuggestPanel({ datasetId, onAddWidget, onClose }: { datasetId?: string; onAddWidget: (widget: any) => void; onClose: () => void }) {
  const { user } = useAuth();
  const isAdmin = modeFor(user) === "admin";
  const qc = useQueryClient();
  const toast = useToast();
  const [picked, setPicked] = useState<string>(datasetId ?? "");
  const [added, setAdded] = useState<Set<number>>(new Set());
  const dsId = datasetId || picked;
  const datasets = useQuery({ queryKey: ["suggest-datasets", isAdmin], queryFn: () => (isAdmin ? datasetApi.list() : datasetApi.listMine()), enabled: !datasetId, staleTime: 5 * 60_000 });
  const q = useQuery({ queryKey: ["suggest-visuals", dsId], queryFn: () => suggestApi.visuals(dsId), enabled: !!dsId, staleTime: 60_000, retry: false });
  const groups = useMemo(() => {
    const out: Array<[string, Array<{ v: SuggestedVisual; i: number }>]> = [];
    (q.data?.visuals ?? []).forEach((v, i) => {
      const g = out.find(([name]) => name === v.group);
      if (g) g[1].push({ v, i });
      else out.push([v.group, [{ v, i }]]);
    });
    return out;
  }, [q.data]);

  const add = useMutation({
    mutationFn: async ({ v }: { v: SuggestedVisual; i: number }) => {
      if (v.measuresToCreate.length) {
        await suggestApi.ensureMeasures(dsId, v.measuresToCreate);
        qc.invalidateQueries({ queryKey: ["studio-measures"] });
      }
      const widget = structuredClone(v.widget);
      widget.id = freshId("w");
      return widget;
    },
    onSuccess: (widget, { i, v }) => {
      onAddWidget(widget);
      setAdded((s) => new Set(s).add(i));
      if (v.measuresToCreate.length) toast.success("Visual added", `Also created the measure “${v.measuresToCreate.map((m) => m.display_name).join("”, “")}” on the dataset.`);
    },
    onError: (e) => toast.error("Couldn't add that visual", e instanceof Error ? e.message : undefined),
  });

  return (
    <div className="ds-card" style={{ padding: 16, maxWidth: 560, width: "100%", maxHeight: "100%", overflow: "auto", boxShadow: "var(--shadow-lg)" }} role="dialog" aria-label="Suggested visuals">
      <div className="ds-row" style={{ alignItems: "flex-start", marginBottom: 10 }}>
        <Sparkles size={18} style={{ color: "var(--accent)", marginTop: 2 }} />
        <div className="ds-grow">
          <div style={{ fontWeight: 700, fontSize: 15 }}>Suggested visuals</div>
          <div className="ds-small ds-muted">Picked from your data: which columns are dates, categories and numbers. Add any you like — you can change everything afterwards.</div>
        </div>
        <Button size="sm" variant="ghost" icon={X} onClick={onClose} aria-label="Close suggestions" />
      </div>
      {!datasetId ? (
        <div style={{ marginBottom: 12 }}>
          <Select value={picked} onChange={(e) => { setPicked(e.target.value); setAdded(new Set()); }} aria-label="Dataset">
            <option value="">Choose the data to build from…</option>
            {(datasets.data ?? []).filter((d) => d.is_active).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </Select>
        </div>
      ) : null}
      {!dsId ? (
        <EmptyState icon={Database} title="Pick a dataset" description="Suggestions are based on the real rows of one dataset." />
      ) : q.isLoading ? (
        <SkeletonRows rows={6} />
      ) : q.isError ? (
        <Banner tone="danger" title="Couldn't make suggestions">{q.error instanceof Error ? q.error.message : null}</Banner>
      ) : q.data ? (
        <>
          <div className="ds-small ds-faint" style={{ marginBottom: 8 }}>
            {q.data.source.rowCount ? `Looked at ${q.data.profile.sampleSize.toLocaleString()} of ${q.data.source.rowCount.toLocaleString()} rows of “${q.data.profile.datasetName}”.` : `Couldn't read rows just now${q.data.source.error ? ` (${q.data.source.error})` : ""} — suggestions use the field settings only.`}
          </div>
          {groups.map(([group, items]) => {
            const Icon = GROUP_ICON[group] ?? Sparkles;
            return (
              <div key={group} style={{ marginTop: 12 }}>
                <div className="ds-small" style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-faint)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><Icon size={13} /> {group}</div>
                <div className="ds-stack" style={{ gap: 6 }}>
                  {items.map(({ v, i }) => (
                    <div key={i} className="ds-row" style={{ alignItems: "center", gap: 10, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 8 }}>
                      <div className="ds-grow" style={{ minWidth: 0 }}>
                        <div className="ds-truncate" style={{ fontWeight: 600, fontSize: 13.5 }}>{v.title}</div>
                        <div className="ds-small ds-muted">{v.why}{v.measuresToCreate.length ? " Creates the measure it needs." : ""}</div>
                      </div>
                      <Button
                        size="sm"
                        variant={added.has(i) ? "ghost" : "secondary"}
                        icon={add.isPending && add.variables?.i === i ? Loader2 : added.has(i) ? Check : Plus}
                        disabled={add.isPending}
                        onClick={() => add.mutate({ v, i })}
                        aria-label={`Add ${v.title}`}
                      >
                        {added.has(i) ? "Added" : "Add"}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {q.data.profile.notes.length ? <div style={{ marginTop: 14 }}><Banner tone="info" title="About your data">{q.data.profile.notes.join(" ")}</Banner></div> : null}
        </>
      ) : null}
    </div>
  );
}
